/** HTTP-клиент публичного API. Без токенов: витрина открыта всем. */

import type {
  BookingResult,
  OffersResponse,
  PropertyCard,
  PropertyDetail,
} from './types'

const BASE = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function toError(response: Response): Promise<ApiError> {
  let code = 'error'
  let message = 'Что-то пошло не так. Попробуйте ещё раз.'
  try {
    const detail = (await response.json())?.detail
    if (typeof detail === 'string') {
      message = detail
    } else if (detail && typeof detail === 'object' && 'message' in detail) {
      code = String(detail.code ?? code)
      message = String(detail.message)
    } else if (Array.isArray(detail)) {
      message = detail.map((d) => d.msg).join('; ')
      code = 'validation_error'
    }
  } catch {
    /* тело не JSON — оставляем общее сообщение */
  }
  return new ApiError(response.status, code, message)
}

type Params = Record<string, string | number | undefined | null>

async function request<T>(
  path: string,
  { params, method = 'GET', body, headers }: {
    params?: Params
    method?: 'GET' | 'POST'
    body?: unknown
    headers?: Record<string, string>
  } = {},
): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
  }

  const response = await fetch(url.toString(), {
    method,
    headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!response.ok) throw await toError(response)
  return (await response.json()) as T
}

export const api = {
  catalog: (params: Params) =>
    request<PropertyCard[]>('/api/v1/public/properties', { params }),

  property: (slug: string, params: Params = {}) =>
    request<PropertyDetail>(`/api/v1/public/properties/${slug}`, { params }),

  offers: (slug: string, params: Params) =>
    request<OffersResponse>(`/api/v1/public/properties/${slug}/offers`, { params }),

  book: (slug: string, body: unknown, idempotencyKey: string) =>
    request<BookingResult>(`/api/v1/public/properties/${slug}/bookings`, {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  bookingStatus: (reference: string, email: string) =>
    request<BookingResult>(`/api/v1/public/bookings/${reference}`, { params: { email } }),
}
